/**
 * `*appCanAccess` — structural directive that renders its content only when the
 * user holds a SCOPED permission in the active organization. The signals-native,
 * scope-aware counterpart to `*ngxPermissionsOnly` (which only does flat string
 * membership): it reads `OrganizationContextStore.can(entity, action, minScope)`.
 *
 * UX gating ONLY — the server stays authoritative (`buildScopeFilter` /
 * `requireOeScope`). ADMIN/SUPER_ADMIN pass (they hold every key at scope `all`).
 *
 * Usage:
 * ```html
 * <!-- defaults to the 'read' action -->
 * <a *appCanAccess="'organigram'">Organigram</a>
 *
 * <!-- explicit action + minimum scope breadth, with an else branch -->
 * <button *appCanAccess="'organigram'; action: 'update'; minScope: 'child_oe'; else noAccess">
 *   Edit
 * </button>
 * <ng-template #noAccess>…</ng-template>
 * ```
 */
import {
  Directive,
  TemplateRef,
  ViewContainerRef,
  effect,
  inject,
  input,
} from '@angular/core';
import type { PermissionAction, PermissionScope } from '@mb/auth/shared';
import { OrganizationContextStore } from '../state/organization-context.state';

@Directive({
  selector: '[appCanAccess]',
  standalone: true,
})
export class CanAccessDirective {
  readonly #template = inject(TemplateRef<unknown>);
  readonly #viewContainer = inject(ViewContainerRef);
  readonly #orgContext = inject(OrganizationContextStore);

  /** Entity key to check, e.g. `'organigram'`. */
  readonly entity = input.required<string>({ alias: 'appCanAccess' });
  /** Action to check; defaults to `'read'`. */
  readonly action = input<PermissionAction>('read', {
    alias: 'appCanAccessAction',
  });
  /** Optional minimum scope breadth (e.g. `'child_oe'`); omit to allow any held scope. */
  readonly minScope = input<PermissionScope | undefined>(undefined, {
    alias: 'appCanAccessMinScope',
  });
  /** Optional template to render when access is denied. */
  readonly elseTemplate = input<TemplateRef<unknown> | null>(null, {
    alias: 'appCanAccessElse',
  });

  /** What's currently in the view — skip rebuilding the embedded view unless it changes. */
  #rendered: 'then' | 'else' | null = null;

  constructor() {
    effect(() => {
      const allowed = this.#orgContext.can(
        this.entity(),
        this.action(),
        this.minScope(),
      );
      const elseTpl = this.elseTemplate();
      const target = allowed ? 'then' : elseTpl ? 'else' : null;
      if (target === this.#rendered) {
        return;
      }
      this.#viewContainer.clear();
      if (target === 'then') {
        this.#viewContainer.createEmbeddedView(this.#template);
      } else if (target === 'else' && elseTpl) {
        this.#viewContainer.createEmbeddedView(elseTpl);
      }
      this.#rendered = target;
    });
  }
}
